import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import ScheduleRepeat from '../repeat';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import { useScheduleForm } from '../../../../contexts/form';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useMetaData } from '../../../../contexts/metadata';

interface EpxHeartData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  mode?: string;
  times: string[];
  days: number[];
  step?: number;
  hasScale: boolean;
  shouldConfirmWeight: boolean;
  askBloodPressure: boolean;
  isPostDischarge?: boolean;
  isBinary?: boolean;
  isConnectEnabled?: boolean;
  shouldUseKCCQ?: boolean;
  isWeightDisabled?: boolean;
}

type EpxHeartDataRaw = DateSerialized<EpxHeartData, 'start' | 'end'>;

export const EpxHeartDefaultData: EpxHeartData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: false,
  mode: 'W',
  times: ['10:00'],
  days: [1, 4],
  step: 1,
  hasScale: true,
  shouldConfirmWeight: true,
  askBloodPressure: false,
  isPostDischarge: false,
  isBinary: false,
  isConnectEnabled: false,
  shouldUseKCCQ: false,
  isWeightDisabled: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxHeartFormProps {
  setForm?: (form: FormInstance<EpxHeartDataRaw>) => void;
  onSubmit?: (data: EpxHeartData) => void;
  isDisabled?: boolean;
}

const EpxHeartForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxHeartFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxHeartDataRaw): EpxHeartData => {
    let mode = data.mode;
    if (!data.isFixed) {
      if (data.isPostDischarge) {
        mode = 'D';
      } else {
        mode = 'W';
      }
    }
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      mode
    };
  };
  const deserialize = (data: EpxHeartData): EpxHeartDataRaw => {
    let isPostDischarge = data.isPostDischarge;
    if (isPostDischarge == null) {
      isPostDischarge = false;
    }
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      isPostDischarge
    };
  };
  const defaultData = deserialize(EpxHeartDefaultData);
  const form = new FormInstance<EpxHeartDataRaw, EpxHeartData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: EpxHeartData): void => {
    if (!data.isFixed && data.mode !== 'W') {
      data.mode = 'W';
      form.setData(data);
    }
    if (data.isFixed) {
      if (data.step == null) {
        data.step = 1;
      }
      if (data.mode == null) {
        data.mode = 'W';
      }
      if (data.days == null) {
        data.days = [];
      }
    }
  };
  const onSubmit = (data: EpxHeartData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        disabled={isDisabled}
      >
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Weight"
                      name="isWeightDisabled"
                      trueLabel="No"
                      falseLabel="Yes"
                      fill
                    />
                    <Callout className="mt-4 mb-4">
                      If no, then the {terms.patient.default} will not be asked
                      any questions relating to weight.
                    </Callout>
                    <SelectBooleanInput
                      label="Ask to confirm Weight"
                      name="shouldConfirmWeight"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4 mb-4">
                      If yes, then the {terms.patient.default} will be asked to
                      confirm their weight quantity entry.
                    </Callout>
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Track Exact Weight"
                      name="hasScale"
                      trueLabel="Yes (Requires Scale)"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4 mb-4">
                      <strong>Note:</strong> you can still specify this setting,
                      but if "Ask about Weight" is turned off then it will not
                      matter either way.
                    </Callout>
                    <SelectBooleanInput
                      label="Ask Extended Shortness of Breath Symptoms"
                      name="askSOBSymptoms"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Ask about Blood Pressure"
                      name="askBloodPressure"
                      trueLabel="Yes (Requires Cuff)"
                      falseLabel="No"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Schedule Type"
                      name="isFixed"
                      trueLabel="Fixed Schedule"
                      falseLabel="SMART Schedule"
                      fill
                    />
                  </Col>
                  {!props.values.isFixed && !isEditMode ? (
                    <Col sm={12}>
                      <SelectBooleanInput
                        label="Patient Is Post-Discharge"
                        name="isPostDischarge"
                        trueLabel="Yes (Start on Daily)"
                        falseLabel="No (Start on 2 Times per Week)"
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
              </CardBlock>
              {props.values.isFixed ? (
                <>
                  <CardBlock>
                    <ScheduleRepeat label="Repeat Messages" />
                  </CardBlock>
                  {props.values.mode === 'W' ? (
                    <CardBreakout>
                      <Weekday name="days" inline />
                    </CardBreakout>
                  ) : null}
                </>
              ) : (
                <CardBreakout>
                  <Weekday
                    label="Choose 2 Days (For Low Frequency)"
                    name="days"
                    numItems={2}
                    inline
                  />
                </CardBreakout>
              )}
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Binary Mode"
                      name="isBinary"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Enable Text-in CONNECT"
                      name="isConnectEnabled"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this means the {terms.patient.default}{' '}
                      will be able to text "CONNECT" to the program phone number
                      in order to proactively send in responses at any time.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Use KCCQ Question"
                      name="shouldUseKCCQ"
                      fill
                    />
                    <Callout>
                      Saying "Yes" to this means the {terms.patient.default}{' '}
                      will receive a question about if shortness of breath or
                      feeling tired has kept them from doing their daily
                      activities. This question will be asked once every two
                      weeks.
                      <br />
                      <strong>Note:</strong> Normally the question is asked on a
                      scale of 0 (never) to 5 (all the time), but if the binary
                      mode is turned on then the question will be asked as a yes
                      or no.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxHeartForm;
